import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function RegistrationModal({ open, onOpenChange, context, onSuccess }) {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setForm({ first_name: "", last_name: "", email: "", phone: "" });
  }, [open]);

  if (!context) return null;
  const { slot, area, ts } = context;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim()) {
      toast.error("Veuillez remplir prénom, nom et email.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/register", { slot_id: slot.id, ...form });
      toast.success("Inscription confirmée. Merci !");
      onSuccess?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409) {
        toast.error(detail || "Ce créneau vient d'être pris par une autre personne.");
        onSuccess?.();
      } else {
        toast.error(detail || "Erreur lors de l'inscription.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="registration-modal" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-[#0b3a73]">S'inscrire à un créneau</DialogTitle>
          <DialogDescription>
            <span className="block text-slate-700">
              <strong>{area.name}</strong> · {ts.label}
            </span>
            <span className="block text-xs text-slate-500 mt-1">
              Vos coordonnées ne seront visibles que par l'organisation.
            </span>
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="registration-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="first_name">Prénom</Label>
              <Input
                id="first_name"
                data-testid="input-first-name"
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="last_name">Nom</Label>
              <Input
                id="last_name"
                data-testid="input-last-name"
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                required
              />
            </div>
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              data-testid="input-email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
            />
          </div>
          <div>
            <Label htmlFor="phone">Téléphone mobile <span className="text-slate-400 font-normal">(facultatif)</span></Label>
            <Input
              id="phone"
              type="tel"
              data-testid="input-phone"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          </div>
          <DialogFooter className="mt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="cancel-registration-btn"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="submit-registration-btn"
              className="bg-[#0b3a73] hover:bg-[#0a2f5c]"
            >
              {submitting ? "Inscription..." : "Valider mon inscription"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
